import { QuoteChartTooltip } from 'components/tooltip/QuoteChartTooltip';
import QuoteChartTooltipCursor from 'components/tooltip/QuoteChartTooltipCursor';
import React, { FC } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Dot,
  DotProps,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartOption, QuoteChartData } from 'types/chart';
import dayjs from 'dayjs';
import { formatDanjiAveragePriceTick } from 'utils/formatUtil';

interface Props {
  lineData: QuoteChartData[];
  scatterData: QuoteChartData[];
  chartKey: string;
  onChartClick: () => void;
  onChartMouseLeave: () => void;
  chartOption: ChartOption;
  isVisibleDay: boolean;
  isVisibleRealTradeTypeChart: boolean;
}

function QuoteChart({
  chartKey,
  chartOption,
  lineData,
  scatterData,
  isVisibleDay,
  isVisibleRealTradeTypeChart,
  onChartClick,
  onChartMouseLeave,
}: Props) {
  const { tooltipActive, xAxisProps, yAxisProps } = chartOption;

  return (
    <ResponsiveContainer key={chartKey}>
      <ComposedChart
        data={lineData}
        onClick={onChartClick}
        onMouseLeave={onChartMouseLeave}
        key={chartKey}
        margin={{ top: 5, right: 0, bottom: 5, left: 5 }}
      >
        <CartesianGrid vertical={false} stroke="#E6E6E6" />

        <XAxis
          dataKey="date"
          type="number"
          allowDuplicatedCategory={false}
          interval={isVisibleDay ? 'preserveEnd' : undefined}
          domain={['auto', 'auto']}
          scale="time"
          axisLine={{ stroke: 'none' }}
          padding={{ right: isVisibleDay ? 15 : 20, left: 10 }}
          tick={{
            fill: '#d7d7d7',
            fontSize: '0.6rem',
            fontWeight: 700,
          }}
          ticks={isVisibleDay ? undefined : xAxisProps?.ticks}
          tickMargin={5}
          tickLine={false}
          tickFormatter={(value) => {
            const yearMonth = dayjs(value).format('YYYY-MM');
            const monthDay = dayjs(value).format(`MM-DD`); // 현재 데이터의 인덱스를 가져옵니다.
            const index = lineData.findIndex((data) => data.date === value);

            // 이전 데이터의 년도를 가져옵니다.
            const prevYear =
              index > 0 ? dayjs(lineData[index - 1].date).format('YYYY') : '';

            if (isVisibleDay) return monthDay;
            // 현재 데이터의 년도와 이전 데이터의 년도가 다를 경우에만 년도를 표시합니다.
            return yearMonth !== prevYear ? yearMonth : '';
          }}
        />
        <YAxis
          yAxisId="left"
          type="number"
          tickCount={yAxisProps?.tickCount}
          interval={0}
          ticks={yAxisProps?.ticks}
          width={40}
          axisLine={false}
          tickLine={false}
          tick={{
            fill: '#d7d7d7',
            fontSize: '0.6rem',
            fontWeight: 700,
          }}
          domain={yAxisProps?.domain}
          tickFormatter={(value) => formatDanjiAveragePriceTick(value)}
        />

        <Tooltip
          active={tooltipActive}
          content={
            <QuoteChartTooltip
              isVisibleDay={isVisibleDay}
              isVisibleRealTradeTypeChart={isVisibleRealTradeTypeChart}
            />
          }
          wrapperStyle={{
            zIndex: 1,
          }}
          cursor={
            <QuoteChartTooltipCursor
              isVisibleRealTradeTypeChart={isVisibleRealTradeTypeChart}
            />
          }
        />

        <Line
          dot={false}
          connectNulls
          type="monotone"
          dataKey="woodaebbangPrice"
          yAxisId="left"
          stroke="#4584FF"
          strokeWidth={3}
          isAnimationActive={false}
        />

        {isVisibleRealTradeTypeChart &&
          scatterData.some((value) => value.realPrice) && (
            <Scatter // Scatter 추가
              isAnimationActive={false}
              data={scatterData}
              yAxisId="left"
              dataKey="realPrice"
              shape={<RenderDot />}
            />
          )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export default QuoteChart;

const RenderDot: FC<DotProps> = ({ cx, cy }) => {
  return <Dot cx={cx} cy={cy} fill="#4584FF" r={3} opacity={0.5} />;
};
